/**
 * HEIC / HEIF conversion utilities.
 *
 * Browsers (except Safari on Apple devices) cannot render HEIC images natively.
 * • convertHeicIfNeeded(file) → converts a File to JPEG before upload if needed.
 * • heicSrcToJpeg(url)       → fetches a HEIC URL and returns a JPEG object URL.
 */

function isHeic(file) {
  const name = (file?.name ?? "").toLowerCase();
  const type = (file?.type ?? "").toLowerCase();
  return (
    type === "image/heic" ||
    type === "image/heif" ||
    name.endsWith(".heic") ||
    name.endsWith(".heif")
  );
}

/**
 * If the file is HEIC/HEIF, convert it to a JPEG File; otherwise return as-is.
 * Uses heic2any (lazy-loaded so it doesn't bloat the initial bundle).
 */
export async function convertHeicIfNeeded(file) {
  if (!isHeic(file)) return file;

  const heic2any = (await import("heic2any")).default;
  const blob = await heic2any({ blob: file, toType: "image/jpeg", quality: 0.92 });
  const outputBlob = Array.isArray(blob) ? blob[0] : blob;
  // Replace .heic/.heif extension with .jpg
  const newName = file.name.replace(/\.(heic|heif)$/i, ".jpg");
  return new File([outputBlob], newName, { type: "image/jpeg" });
}

/**
 * Given a src URL, returns a displayable src:
 * - If the URL points to a HEIC file, fetches it, converts to JPEG, returns an object URL.
 * - Otherwise returns the original URL unchanged.
 *
 * NOTE: The caller is responsible for revoking the object URL when done (URL.revokeObjectURL).
 */
export async function heicSrcToJpeg(url) {
  if (!url) return url;
  const lower = url.toLowerCase();
  if (!lower.includes(".heic") && !lower.includes(".heif")) return url;

  try {
    const response = await fetch(url);
    const blob = await response.blob();
    const heic2any = (await import("heic2any")).default;
    const jpegBlob = await heic2any({ blob, toType: "image/jpeg", quality: 0.92 });
    const output = Array.isArray(jpegBlob) ? jpegBlob[0] : jpegBlob;
    return URL.createObjectURL(output);
  } catch (e) {
    console.warn("HEIC conversion failed for", url, e);
    return url; // fallback to original
  }
}
